import type { Translations } from './it'

export const es: Translations = {
  common: {
    ctaLabel: "Hablemos",
    freeNote: "15 minutos de consulta gratuita, sin compromiso.",
  },
  header: {
    backToTop: "Volver arriba",
    logoAlt: "Fra un viaggio e l’altro — Francesca Zaccari, travel designer",
    logoAltShort: "Fra un viaggio e l’altro",
    mainNavAria: "Navegación principal",
    mobileNavAria: "Navegación móvil",
    openMenu: "Abrir el menú",
    closeMenu: "Cerrar el menú",
    languageLabel: "Cambiar idioma",
    nav: {
      about: "Quién soy",
      services: "Servicios",
      itineraries: "Itinerarios",
      how: "Cómo funciona",
      reviews: "Reseñas",
    },
  },
  hero: {
    eyebrow: "Travel Designer · Acompañante turística",
    greetingBefore: "Hola, me llamo ",
    greetingAccent: "Francesca",
    titleBefore: "Tu viaje",
    titleAccent: "a través del arte.",
    paragraph:
      "Me llamo Francesca, soy Travel Designer y construyo itinerarios a medida que leen un lugar a través de su arte y su historia. Nada de paquetes «prefabricados», solo viajes que te dejan algo.",
    paragraphMobile:
      "Soy Travel Designer y construyo itinerarios a medida que leen un lugar a través de su arte y su historia. Nada de paquetes «prefabricados», solo viajes que te dejan algo.",
    colosseumAlt: "El Coliseo de Roma al atardecer",
    positanoAlt: "Las casas de colores de Positano frente al mar",
    polaroidCaption: "Costa Amalfitana",
    badge: "viajes de arte por el mundo",
    girlAlt: "Ilustración de una viajera con pañuelo naranja que sostiene un libro de arte",
  },
  about: {
    eyebrow: "Quién soy",
    titleBefore: "Hola, soy Francesca,",
    titleAccent: "historiadora del arte de viaje.",
    p1: "Estudié en la Universidad de Bolonia, donde obtuve dos titulaciones: Patrimonio Cultural y Artes Visuales. En la práctica, pasé años aprendiendo a mirar las ciudades como se mira un cuadro, y ya no he dejado de hacerlo.",
    p2: "Después de la carrera viví un año en Canadá; luego volví e hice el curso de Travel Designer en Milán: ahí aprendí el oficio de construir itinerarios a medida, pieza a pieza. En Rávena obtuve la licencia de acompañante turística.",
    p3Before:
      "Hoy trabajo como acompañante en food tours para varias agencias y acompaño grupos al extranjero.",
    p3Brand: "Fra un viaggio e l’altro",
    p3After:
      "es mi proyecto personal: joven y hecho con mimo, igual que los itinerarios que diseño a medida.",
    portraitAlt: "Francesca Zaccari, travel designer y acompañante turística",
    portraitMissing: "foto muy pronto",
    mapAlt: "Mapa de viaje con notas e itinerarios marcados a mano",
    mapCaption: "apuntes de ruta",
    badgeAria: "Travel Designer certificada",
    steps: [
      { place: "Bologna", text: "Dos titulaciones en Patrimonio Cultural y Artes Visuales" },
      { place: "Canada", text: "Un año viviendo en el extranjero tras la carrera" },
      { place: "Milano", text: "Curso de Travel Designer: itinerarios a medida" },
      { place: "Ravenna", text: "Licencia de acompañante turística" },
    ],
  },
  concept: {
    eyebrow: "El concepto",
    titleBefore: "No un paquete.",
    titleAccent: "Un punto de vista.",
    paragraph:
      "Uno viaje, arte y cultura: cada itinerario cuenta un lugar a través de una clave artística o histórica. No una lista de cosas que ver, sino una historia que recorrer — con el ritmo justo, contexto y algún desvío que vale el viaje.",
    points: [
      {
        title: "Itinerarios culturales y artísticos",
        text: "Cada viaje tiene su clave de lectura: un artista, una época, un movimiento.",
      },
      {
        title: "Para viajeros internacionales y grupos",
        text: "Diseño y acompaño recorridos para quien llega de lejos y para quien viaja en compañía.",
      },
      {
        title: "Método de historiadora del arte",
        text: "Fuentes, contextos, detalles: el mismo cuidado con el que se estudia una obra.",
      },
    ],
    badge: "ejemplo de itinerario",
    cardTitle: "Tras las huellas de Caravaggio",
    cardText:
      "Un itinerario entre Roma y Nápoles para explorar las dos ciudades con los ojos del pintor: los lienzos en las iglesias, las calles que recorrió, la luz que cambió la pintura.",
    cardMeta: "Roma → Nápoles · arte · historia",
    galleryAlt: "Interior de una galería de arte italiana con cuadros en las paredes",
    cinqueTerreAlt: "Los pueblos de colores de las Cinque Terre frente al mar",
    discoverCta: "Descubre más",
  },
  services: {
    eyebrow: "Servicios",
    titleBefore: "Lo que puedo hacer",
    titleAccent: "por ti",
    items: [
      {
        title: "Travel Design",
        description:
          "Un itinerario de viaje totalmente personalizado, construido a medida según quién eres, qué te interesa y cómo te gusta viajar. Nada de paquetes prefabricados: estudio etapas, tiempos y experiencias para ti. Las reservas de los servicios pasan luego por agencia, tú no tienes que pensar en nada.",
      },
      {
        title: "Acompañamiento turístico",
        description:
          "Además de diseñar el itinerario, puedo acompañarte físicamente a ti o a tu grupo, en Italia y en el extranjero. Museos, ciudades de arte, rutas fuera de los caminos trillados, cultura gastronómica: viajo contigo y me aseguro de que la logística del viaje funcione.",
      },
      {
        title: "Revisión de itinerario",
        description:
          "¿Ya tienes un plan de viaje? Lo miro con ojos de experta y te digo con franqueza qué funciona, qué no y qué falta: etapas demasiado ajustadas, horarios que no cuadran, joyas que te arriesgas a perderte a dos pasos de donde estarás.",
      },
      {
        title: "Guías de viaje",
        description:
          "Contenidos y recursos de viaje para leer entre un viaje y otro: guías, consejos prácticos y miradas de arte para llegar preparado y ver cada lugar con otros ojos.",
      },
    ],
    bottomText: "¿No sabes por dónde empezar?",
    discoverCta: "Descubre los servicios",
  },
  servicesPage: {
    eyebrow: "Los servicios en detalle",
    titleBefore: "Cuatro formas de viajar",
    titleAccent: "conmigo.",
    intro:
      "Cada viaje es diferente, y cada viajero necesita algo distinto: hay quien quiere un itinerario construido desde cero, quien prefiere una guía a su lado y quien solo busca una segunda opinión. Aquí encontrarás en detalle lo que puedo hacer por ti — con un ejemplo concreto para cada servicio.",
    exampleLabel: "Un ejemplo concreto",
    idealForLabel: "Para quién es",
    items: [
      {
        example:
          "Una semana en París tras las huellas de los Impresionistas: el Museo de Orsay a primera hora, lejos de las multitudes; un paseo por Montmartre, donde estos pintores vivían y trabajaban; un día en Giverny, entre los jardines de Monet. Ritmo relajado, con tiempo para los cafés históricos y las librerías de la Rive Gauche.",
        idealFor: "Para quien quiere un viaje construido a medida, sin pensar en nada.",
      },
      {
        example:
          "Tres días en Florencia con un grupo de amigos: los Uffizi contados con calma, sala por sala; el Oltrarno de los artesanos, lejos de las rutas más transitadas; un atardecer desde un mirador que no aparece en las guías. Viajo con vosotros y os cuento lo que vemos, como historiadora del arte.",
        idealFor: "Para grupos, familias y viajeros que quieren una guía experta a su lado.",
      },
      {
        example:
          "¿Habías planeado Roma en dos días con ocho paradas al día? Lo recalibro contigo: elimino las carreras inútiles, reordeno las visitas por zonas y añado esa iglesia con dos Caravaggio que te arriesgabas a perderte a dos pasos de tu hotel.",
        idealFor: "Para quien ya tiene un plan de viaje pero quiere una mirada experta antes de partir.",
      },
      {
        example:
          "Una guía digital a medida antes de la salida: qué ver y en qué orden, cómo moverte, dónde comer y las claves de lectura artística de cada etapa — para consultar sin conexión, directamente desde el teléfono.",
        idealFor: "Para los viajeros independientes que quieren partir preparados.",
      },
    ],
    ctaTitle: "¿No sabes cuál es para ti?",
    ctaSubtitle:
      "Cuéntamelo en una charla gratuita de 15 minutos: entenderemos juntos por dónde empezar.",
    backHome: "Volver a la home",
  },
  itinerariesPage: {
    eyebrow: "Itinerarios de viaje artístico-culturales",
    titleBefore: "Viajes con",
    titleAccent: "un punto de vista.",
    intro: "Estos son ejemplos de itinerarios que puedo construir y personalizar contigo.",
    detailsLabel: "La experiencia",
    durationLabel: "Duración orientativa",
    backHome: "Volver a la home",
    closeModalAria: "Cerrar detalles del itinerario",
    itineraryLabel: "Itinerario",
    discoverMore: "Descubrir más",
    ctaEyebrow: "Construyamos tu viaje",
    ctaTitle: "¿Ya tienes un destino en mente?",
    ctaText: "Partamos de tus deseos y transformémoslos en un itinerario a medida.",
    items: [
      { title: "Caravaggio entre Roma y Nápoles", description: "Iglesias, plazas y obras maestras contadas a través de la luz y la historia del pintor.", details: "Un recorrido que parte de Roma y nos muestra los inicios del pintor entre los Museos Capitolinos, las iglesias y la Galería Borghese, para llegar después a Nápoles, donde Caravaggio por fin se siente comprendido.", duration: "5 días", cover: "/images/caravaggio-giuditta.jpg", coverAlt: "Judith decapitando a Holofernes, Caravaggio" },
      { title: "Impresionistas en Provenza", description: "De Cézanne a Van Gogh, la Provenza y sus campos de lavanda en flor vistos a través de sus ojos.", details: "Un itinerario entre las ciudades que inspiraron a los grandes maestros del impresionismo, para leer un paisaje que cambió la manera de ver el arte.", duration: "5–7 días", cover: "/uploads/eabb27de4d.jpg", coverAlt: "Campo de trigo con vista de Saint-Rémy, Van Gogh" },
      { title: "Sobre las huellas de la abadía de Pomposa", description: "Tres itinerarios de un día, un único hilo conductor: la abadía de Pomposa, faro de fe y cultura en la Edad Media entre el Po y el mar.", details: "1 · Giotto entre Padua y Pomposa\nDesde Padua, entre la Capilla de los Scrovegni y la Basílica de San Antonio, hasta Pomposa: sobre las huellas de las influencias del pintor a lo largo de la costa romañola.\n\n2 · El mosaico entre Rávena y Pomposa\nSe parte de los mosaicos bizantinos y paleocristianos de Rávena y de Sant’Apollinare in Classe, para llegar a Pomposa ante el pavimento musivo medieval: un recorrido que cierra un círculo abierto siglos antes.\n\n3 · Dante, el exilio y los últimos días\nDesde Rávena, en los lugares que lo acogieron durante la redacción de la Comedia, hasta Pomposa, donde conoció su infierno personal: allí se enferma y poco después morirá.", duration: "3 itinerarios · 1 día cada uno", cover: "/uploads/252221080f.jpg", coverAlt: "Interior de la basílica con frescos medievales" },
      { title: "Florencia renacentista", description: "La ciudad de los Médicis a través de artistas, talleres y arquitectura.", details: "Uffizi, Oltrarno, capillas y talleres artesanos con un ritmo equilibrado y las pausas adecuadas.", duration: "2–4 días", cover: "/uploads/e89d5c2b97.jpg", coverAlt: "Panorama de Florencia con el Duomo" },
      { title: "Venecia entre arte y agua", description: "Una Venecia para recorrer con curiosidad, entre grandes obras maestras y detalles escondidos.", details: "San Marco, Dorsoduro, islas de la laguna y rutas alternativas para descubrir la ciudad sin prisas.", duration: "3 días", cover: "/images/venezia.jpg", coverAlt: "Un canal de Venecia" },
      { title: "Cinque Terre más allá de la postal", description: "Pueblos, senderos y paisajes ligures con un ritmo lento y consciente.", details: "Un itinerario entre mar, cultura local y pequeños productores, lejos de las paradas más concurridas.", duration: "3–5 días", cover: "/images/cinque-terre.jpg", coverAlt: "Los pueblos coloridos de Cinque Terre" },
    ],
  },
  collaborations: {
    navLabel: "Colaboraciones",
    eyebrow: "Colaboraciones",
    titleBefore: "Las realidades que",
    titleAccent: "viajan conmigo.",
    intro:
      "Además de los viajes a medida, trabajo con tour operadores y realidades locales que comparten mi manera de viajar. Aquí encuentras todos los tours y grupos con los que parto, reservables directamente con ellos.",
    backHome: "Volver a la home",
    homeEyebrow: "Colaboraciones",
    homeTitleBefore: "También viajo",
    homeTitleAccent: "en compañía.",
    homeText:
      "Food tours en Emilia-Romaña, tours en grupo por Italia y por el mundo, coordinadora de viaje: algunas de mis salidas son reservables directamente con las realidades con las que colaboro.",
    homeCta: "Descubre las colaboraciones",
    foodTours: {
      title: "Food tours",
      role: "Acompañante turística",
      intro:
        "La comida es cultura: acompaño food tours en Emilia-Romaña, entre mercados históricos, tiendas y mesas servidas. Cada tour es reservable directamente con la organización.",
      partners: [
        {
          place: "Rímini · con Hili",
          text: "En Rímini recorro con Hili las paradas del centro histórico: pescado fresco, piadina y las historias de la ciudad, en un tour que une sabor y curiosidad.",
          code: "",
          codeLabel: "",
          links: [
            { label: "Get Your Guide", url: "https://gyg.me/GOCSOAtk" },
            { label: "Viator", url: "https://www.viator.com/it-IT/tours/Rimini/Rimini-Walking-Food-Tour-and-Highlights/d30027-326991P39" },
          ],
        },
        {
          place: "Bolonia · con Do Eat Better",
          text: "En Bolonia guío el tour gastronómico de Do Eat Better Experience: los sabores de la ciudad contados entre los puestos del mercado y las tiendas del centro. Con mi código tienes un 5% de descuento.",
          code: "FRANCIZ5BOLOGNA",
          codeLabel: "Código de descuento del 5%: ",
          links: [
            { label: "Do Eat Better Experience", url: "https://doeatbetterexperience.com/italy-food-tour/what-to-do-in-bologna/bologna-food-tour/" },
          ],
        },
        {
          place: "Bolonia · con Tours and The City",
          text: "Con Tours and The City guío la Bologna Gastronomic Experience: mercados históricos, tiendas y recetas de la tradición contadas en la mesa.",
          code: "",
          codeLabel: "",
          links: [
            { label: "Tours and The City", url: "https://toursandthecity.com/bologna-gastronomic-experience-with-a-local/" },
          ],
        },
        {
          place: "Bolonia · con Taste Bologna",
          text: "Con Taste Bologna acompaño el Classic Bologna Food Tour: osterias históricas, mercados y tiendas familiares, entre parmesano, mortadela y tortellini hechos a mano, en grupos pequeños.",
          code: "",
          codeLabel: "",
          links: [
            { label: "Taste Bologna", url: "https://www.tastebologna.net/classic-bologna" },
          ],
        },
      ],
    },
    village: {
      title: "The Village",
      role: "Acompañante turística",
      text: "Para The Village acompaño los tours en Emilia-Romaña, con salidas desde Bolonia y Rávena: viajes breves y cuidados para descubrir las maravillas de la región.",
      linkLabel: "The Village Italy",
      url: "https://www.thevillageitaly.com/",
    },
    realWanderlust: {
      title: "Real Wanderlust",
      role: "Coordinadora de viajes en grupo",
      text: "Acompaño los viajes en grupo de Real Wanderlust por todo el mundo, entre itinerarios cuidados y espíritu libre.",
      linkLabel: "Real Wanderlust",
      url: "https://www.realwanderlust.it/",
    },
    theVoyage: {
      title: "The Voyage",
      role: "Experta local de Bolonia",
      text: "Con The Voyage muestro Bolonia a viajeros de todo el mundo: tours entre los pórticos, el mercado y las historias de la ciudad más estrecha de Italia.",
      linkLabel: "Mi perfil en The Voyage",
      url: "https://www.thevoyage.co/locals/francesca-zaccari",
    },
    aurea: {
      title: "Aurea Viaggi",
      role: "Acompa\u00f1ante tur\u00edstica",
      text: "Con Aurea Viaggi acompa\u00f1o viajes en grupo a paso lento: destinos de arte y naturaleza vividos con calma, con tiempos c\u00f3modos y relatos cuidados, pensados para quien prefiere saborear cada parada sin prisa.",
      linkLabel: "Aurea Viaggi",
      url: "https://aureaviaggicastenaso.my.canva.site/",
      tour: {
        eyebrow: "Pr\u00f3ximo tour",
        title: "La Cascada de Marmore, dedicada al agua",
        text: "Del 9 al 11 de abril de 2027 exploramos juntos la Cascada de Marmore, celebrada por los poetas a través del agua: tierra, arte y naturaleza.",
        email: "fra.unviaggioelaltro@gmail.com",
        emailSubject: "Tour Cascada de las M\u00e1rmol \u2014 Aurea Viaggi, 9-11 de abril",
        emailLabel: "Escr\u00edbeme para m\u00e1s informaci\u00f3n",
      },
    },
    weroad: {
      title: "WeRoad",
      role: "Coordinadora de viaje en grupo",
      text: "En WeRoad parto como coordinadora de viaje: grupos de gente de mi edad, itinerarios pensados para vivir el destino juntos y un toque de aventura.",
      linkLabel: "Próximas salidas — perfil de coordinadora",
      url: "https://www.weroad.it/coordinatori/francesca-zaccari",
    },
  },
  process: {
    eyebrow: "El camino",
    titleBefore: "Cómo",
    titleAccent: "funciona",
    steps: [
      {
        title: "Nos conocemos",
        description:
          "Quince minutos de consulta gratuita: me cuentas a dónde quieres ir, qué te gusta y cómo viajas. Sin compromiso, sirve para entender si puedo serte útil.",
      },
      {
        title: "Diseño tu viaje",
        description:
          "Construyo el itinerario a medida: etapas, experiencias, ritmos. Nada de copiar y pegar, cada viaje parte de cero y de quien lo va a vivir.",
      },
      {
        title: "Nos vamos",
        description:
          "El itinerario está listo. Las reservas pasan por agencia, tú solo piensa en hacer la maleta y partir.",
      },
    ],
  },
  reviews: {
    eyebrow: "Reseñas",
    titleBefore: "Palabras de quien ha viajado",
    titleAccent: "conmigo",
    prevAria: "Reseña anterior",
    nextAria: "Reseña siguiente",
    goToAria: "Ir a la reseña {n}",
    items: [
      {
        name: "Noemi G.",
        initial: "N",
        trip: "Budapest, 3 días a medida",
        text: "Francesca supo adaptarse a mis necesidades. Después de decidir juntas qué atracciones y lugares emblemáticos de Budapest visitar, creó un itinerario a medida de 3 días. Los cambios que tuvimos que hacer sobre la marcha (como intercambiar alguna visita entre el segundo y el tercer día) no estropearon la experiencia. Al contrario, me permitieron seguir mi propio ritmo y disfrutar al máximo de la capital.",
      },
      {
        name: "Giulia Z.",
        initial: "G",
        trip: "Tour de Irlanda en preparación",
        text: "Llevo 11 meses viviendo en Dublín, pero nunca había tenido la oportunidad de conocer mejor Irlanda. Así que confié en Francesca, que está organizando un tour según mis necesidades y preferencias. Me mantiene al tanto de cada cambio y novedad. Ha entendido exactamente qué tipo de experiencia quiero vivir y lo está planificando todo con gran pasión y dedicación. A estas alturas, no veo la hora de visitar todos los lugares que Francesca habrá descubierto entre los paisajes fascinantes de Irlanda.",
      },
      {
        name: "Soana G.",
        initial: "S",
        trip: "Presupuesto detallado",
        text: "Tengo que felicitar a Francesca porque su presupuesto fue muy detallado y cuidado hasta el último detalle... Muy, muy buena, la recomiendo a todos los que sueñan con hacer un viaje, porque ella intenta cumplir tu sueño haciendo que el viaje sea una experiencia única.",
      },
      {
        name: "Luca C.",
        initial: "L",
        trip: "Vietnam y Filipinas, revisión de itinerario",
        text: "Hice la consulta gratuita con Francesca y luego decidí hacer la revisión del itinerario. Aunque mi viaje a Vietnam y Filipinas estaba organizado muy a última hora, con desplazamientos y hoteles reservados pocos días antes de salir, logró reorganizar el itinerario de la mejor manera y pudimos ver todo lo que queríamos de forma óptima. Su consulta fue necesaria para las conexiones de los vuelos y a partir de esa entendimos cómo movernos.",
      },
      {
        name: "Marica D.",
        initial: "M",
        trip: "Bolonia, tour de ciudad con acompañamiento",
        text: "Tuve la oportunidad de conocer a Francesca ya durante un viaje de grupo en agosto y, después de esa primera experiencia, tuve el placer de encontrarla de nuevo como acompañante para descubrir Bolonia, mientras yo estaba allí por trabajo. Fue una experiencia maravillosa: Francesca me acompañó por los principales puntos de interés de la ciudad, contándome sus curiosidades con pasión y competencia. La visita fue agradable y nunca banal, también gracias a su manera de ser muy disponible y a su capacidad de hacer sentir a gusto. Por supuesto, no faltó la parte gastronómica: probamos varias especialidades de la cocina boloñesa, que hicieron el día aún más completo y auténtico. Fue un verdadero placer pasar tiempo con Francesca y descubrir Bolonia a través de sus relatos y su punto de vista. Una persona preparada, solar y apasionada, que recomiendo absolutamente a quien quiera conocer Bolonia no solo como turista, sino también a través de sus historias, sus tradiciones y su cocina.",
      },
    ],
  },
  ctaBand: {
    title: "¿Tienes un viaje en mente?",
    subtitle: "Cuéntamelo en una consulta gratuita de 15 minutos.",
  },
  finalCta: {
    quote:
      "[…] Bajo el azul denso del cielo pasa un ave marina; nunca se detiene: porque todas las imágenes llevan escrito: «más allá».",
    titleBefore: "¿Lista para partir?",
    titleAccent: "Tu viaje empieza aquí.",
    subtext: "Sin compromiso de compra.",
    polaroidCaption: "Nuevos puntos de vista",
    positanoAlt: "Una panorámica impresionante descubierta durante un viaje",
    veneziaAlt: "Un canal de Venecia al atardecer",
  },
  privacy: {
    backLink: "Volver a la página de inicio",
    seoTitle: "Política de privacidad — Fra un viaggio e l’altro",
    title:
      "INFORMACIÓN SOBRE EL TRATAMIENTO DE DATOS PERSONALES CONFORME AL ARTÍCULO 13 DEL REGLAMENTO (UE) 2016/679",
    intro:
      "La presente Política de Privacidad describe cómo Fra un viaggio e l’altro recoge, utiliza, protege y comparte los datos personales de los usuarios que visitan el sitio web www.fraunviaggioelaltro.it, en pleno respeto del Reglamento (UE) 2016/679 (RGPD) y de la normativa italiana en materia de protección de datos personales.",
    sections: [
      {
        heading: "Responsable del tratamiento",
        paragraphs: ["El responsable del tratamiento de los datos es:"],
        bullets: ["Fra un viaggio e l’altro", "Francesca Zaccari", "fra.unviaggioelaltro@gmail.com"],
        outro: "",
      },
      {
        heading: "Tipos de datos recogidos",
        paragraphs: [
          "Durante la navegación y/o el uso de los servicios ofrecidos por el sitio, podemos recoger las siguientes categorías de datos:",
        ],
        bullets: [
          "Datos identificativos: nombre, apellidos, correo electrónico, número de teléfono (p. ej., formulario de contacto o solicitud de información).",
          "Datos de navegación: dirección IP, tipo de navegador, datos relativos a la sesión, páginas visitadas (mediante cookies técnicas o analíticas).",
          "Datos facilitados voluntariamente: mensajes, comentarios, reseñas, reservas, suscripción al boletín.",
        ],
        outro: "",
      },
      {
        heading: "Finalidades del tratamiento",
        paragraphs: [
          "Los datos personales recogidos se tratan con las siguientes finalidades:",
        ],
        bullets: [
          "Responder a las solicitudes enviadas a través del formulario de contacto;",
          "Proporcionar información o presupuestos sobre viajes y servicios turísticos;",
          "Inscribir al usuario en el boletín (previo consentimiento);",
          "Mejorar la experiencia del usuario y las funcionalidades del sitio;",
          "Cumplir obligaciones legales o fiscales.",
        ],
        outro: "",
      },
      {
        heading: "Base jurídica del tratamiento",
        paragraphs: ["El tratamiento de los datos se basa en:"],
        bullets: [
          "Consentimiento expreso del interesado (p. ej., suscripción al boletín);",
          "Ejecución de medidas precontractuales o contractuales (p. ej., solicitud de presupuesto o información);",
          "Obligaciones legales a las que está sujeto el responsable del tratamiento;",
          "Interés legítimo del responsable del tratamiento, en el caso de análisis estadísticos anónimos para mejorar los servicios.",
        ],
        outro: "",
      },
      {
        heading: "Modalidades del tratamiento",
        paragraphs: [
          "El tratamiento de los datos se realiza de manera lícita, leal y transparente, mediante instrumentos manuales e informáticos. Se adoptan medidas técnicas y organizativas adecuadas para garantizar la seguridad y la confidencialidad de los datos, evitando accesos no autorizados, pérdidas o divulgaciones.",
        ],
        bullets: [],
        outro: "",
      },
      {
        heading: "Comunicación y difusión de los datos",
        paragraphs: [
          "Los datos personales no se difundirán nunca a terceros, salvo en los siguientes casos:",
        ],
        bullets: [
          "Sujetos que presten servicios por cuenta del responsable del tratamiento (p. ej., alojamiento web, gestión del boletín, consultores informáticos);",
          "Obligaciones legales, autoridades judiciales o administraciones públicas;",
          "Colaboradores o socios estrechamente vinculados a la gestión de los servicios ofrecidos (p. ej., agencias de viajes u operadores turísticos).",
        ],
        outro:
          "Todos los sujetos externos que traten datos por cuenta del responsable del tratamiento son nombrados Encargados del tratamiento y garantizan medidas de protección adecuadas.",
      },
      {
        heading: "Conservación de los datos",
        paragraphs: [
          "Los datos se conservarán durante el tiempo estrictamente necesario para alcanzar las finalidades indicadas, salvo distintas obligaciones legales. En particular:",
        ],
        bullets: [
          "Datos de contacto: hasta la resolución de la solicitud o la retirada del consentimiento;",
          "Datos del boletín: hasta la baja;",
          "Datos de navegación: según los plazos definidos en la Política de Cookies.",
        ],
        outro: "",
      },
      {
        heading: "Derechos del interesado",
        paragraphs: [
          "De conformidad con los artículos 15-22 del RGPD, el usuario puede ejercer en cualquier momento los siguientes derechos:",
        ],
        bullets: [
          "Acceso a sus datos personales;",
          "Rectificación o actualización;",
          "Supresión («derecho al olvido»);",
          "Limitación del tratamiento;",
          "Oposición al tratamiento;",
          "Portabilidad de los datos;",
          "Retirada del consentimiento (sin perjuicio de la licitud del tratamiento anterior);",
          "Reclamación ante la Autoridad Garante italiana para la Protección de Datos Personales.",
        ],
        outro:
          "Para ejercer sus derechos, puede enviar una solicitud a la dirección de correo electrónico fra.unviaggioelaltro@gmail.com.",
      },
      {
        heading: "Transferencia de datos fuera de la UE",
        paragraphs: [
          "Por norma general, los datos no se transfieren fuera del Espacio Económico Europeo. En caso de necesidad, la transferencia se realizará respetando las garantías previstas por el RGPD (p. ej., decisiones de adecuación, cláusulas contractuales tipo).",
        ],
        bullets: [],
        outro: "",
      },
      {
        heading: "Política de Cookies",
        paragraphs: [
          "Este sitio utiliza cookies técnicas y, previo consentimiento, también cookies de perfilación o analíticas. Para más información y para gestionar sus preferencias, consulte la Política de Cookies dedicada.",
        ],
        bullets: [],
        outro: "",
      },
      {
        heading: "Modificaciones de la presente información",
        paragraphs: [
          "Fra un viaggio e l’altro se reserva el derecho de modificar la presente Política de Privacidad en cualquier momento, publicando la versión actualizada en el sitio. Se invita a los usuarios a consultar esta página con regularidad.",
        ],
        bullets: [],
        outro: "",
      },
    ],
  },
  notFound: {
    title: "Página no encontrada",
    message: "La página {path} no existe.",
    goHome: "Volver al inicio",
    goBack: "Volver atrás",
  },
  footer: {
    privacyLink: "Política de privacidad",
    logoAlt: "Fra un viaggio e l’altro — logo",
    copyright:
      "© {year} Fra un viaggio e l’altro · Francesca Zaccari — Travel Designer & Acompañante turística · IVA 02803860390",
  },
}
